import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { CreditFacilitySetupFooter } from "features/credit-facility/setup/CreditFacilitySetupFooter";
import { CreditFacilitySetupPageProps } from "features/credit-facility/setup/CreditFacilitySetupPageProps";
import {
  getCreditFacilityRetrieveQueryKey,
  useCreditFacilityUpsert,
} from "gen/endpoints/credit-facility/credit-facility";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import { CreditFacilityUpsertDataRequest } from "gen/model/creditFacilityUpsertDataRequest";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
} from "shared/components/content/ContentBlock";
import Form from "shared/components/form/Form";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormMoneyInput } from "shared/components/input/text/MoneyInput";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { usePageWizard } from "shared/components/layout/PageWizard";
import { z as zod } from "zod";

export const CreditFacilitySetupCommitments: React.FC = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["creditFacility"]);

  const { fundFamilyId, creditFacilityId, creditFacility } =
    useOutletContext<CreditFacilitySetupPageProps>();

  const { navigateNext } = usePageWizard();

  const form = useForm<CreditFacilityUpsertDataRequest>({
    defaultValues: creditFacility,
    resolver: zodResolver(
      zod.object({
        borrowing_entity_ids: zod.array(zod.string()).min(1),
        total_commitments: zod.string().min(1),
      }),
    ),
  });

  const { mutateAsync: updateCreditFacility } = useCreditFacilityUpsert();
  const { data: entitiesData } = useFundListSuspense({
    fund_family_ids: [fundFamilyId],
  });

  return (
    <PageContent>
      <PageHeader
        workflowName={t("creditFacility:setup.title")}
        title={t("creditFacility:setup.commitments.title")}
      />
      <PageBody>
        <Form
          form={form}
          onSubmit={async (data: CreditFacilityUpsertDataRequest) => {
            await updateCreditFacility({
              fundFamilyId,
              data,
            });
            await queryClient.invalidateQueries({
              queryKey: getCreditFacilityRetrieveQueryKey(
                fundFamilyId,
                creditFacilityId,
              ),
            });
          }}
          onSuccess={navigateNext}
        >
          <ContentBlockGroup>
            <ContentBlock>
              <ContentBlockBody variant="form">
                <FormMoneyInput
                  name="total_commitments"
                  title={t("creditFacility:setup.commitments.totalCommitment")}
                />
                <FormSelect
                  isMulti={true}
                  title={t(
                    "creditFacility:setup.commitments.entitiesPartyToFacility",
                  )}
                  name="borrowing_entity_ids"
                >
                  {entitiesData.map((entity) => (
                    <SelectOption key={entity.id} value={entity.id!}>
                      {entity.legal_name}
                    </SelectOption>
                  ))}
                </FormSelect>
              </ContentBlockBody>
            </ContentBlock>
          </ContentBlockGroup>
          <CreditFacilitySetupFooter />
        </Form>
      </PageBody>
    </PageContent>
  );
};
